
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigRequest} from './ConfigRequest';
import {FieldModelConfigRequest} from './ConfigRequest';
import {MarginRateProfileGetResponse} from './MarginRateProfileGetResponse';

/**
 * MarginRateProfileGetRequest struct
 */
class MarginRateProfileGetRequest extends ConfigRequest {
  /**
   * Initialize struct
   * @param {!ConfigRequest=} parent
   * @param {UUID=} marginRateProfileId
   * @param {string=} marginRateProfileName
   * @constructor
   */
  constructor (parent = new ConfigRequest(), marginRateProfileId = null, marginRateProfileName = null) {
    super()
    super.copy(parent)
    this.MarginRateProfileId = marginRateProfileId
    this.MarginRateProfileName = marginRateProfileName
  }

  /**
   * Copy struct (shallow copy)
   * @this {!MarginRateProfileGetRequest}
   * @param {!MarginRateProfileGetRequest} other Other struct
   * @returns {!MarginRateProfileGetRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.MarginRateProfileId != null) {
      this.MarginRateProfileId = new UUID(other.MarginRateProfileId)
    } else {
      this.MarginRateProfileId = null
    }
    if (other.MarginRateProfileName != null) {
      this.MarginRateProfileName = other.MarginRateProfileName
    } else {
      this.MarginRateProfileName = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!MarginRateProfileGetRequest}
   * @returns {!MarginRateProfileGetRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new MarginRateProfileGetRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new MarginRateProfileGetRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!MarginRateProfileGetRequest}
   * @param {!MarginRateProfileGetRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof MarginRateProfileGetRequest)) {
      throw new TypeError('Instance of MarginRateProfileGetRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!MarginRateProfileGetRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      MarginRateProfileId: ((this.MarginRateProfileId != null) ? this.MarginRateProfileId.toString() : null),
      MarginRateProfileName: ((this.MarginRateProfileName != null) ? this.MarginRateProfileName : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return MarginRateProfileGetRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!MarginRateProfileGetRequest} other Object value
   * @returns {!MarginRateProfileGetRequest} Created struct
   */
  static fromObject (other) {
    return new MarginRateProfileGetRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!MarginRateProfileGetRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return MarginRateProfileGetRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!MarginRateProfileGetRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6384
  }
}

export { MarginRateProfileGetRequest };

/**
 * Fast Binary Encoding MarginRateProfileGetRequest field model
 */
class FieldModelMarginRateProfileGetRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigRequest(buffer, 4 + 4)
    this.MarginRateProfileId = new fbe.FieldModelOptional(new fbe.FieldModelUUID(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4), buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.MarginRateProfileName = new fbe.FieldModelOptional(new fbe.FieldModelString(buffer, this.MarginRateProfileId.fbeOffset + this.MarginRateProfileId.fbeSize), buffer, this.MarginRateProfileId.fbeOffset + this.MarginRateProfileId.fbeSize)
  }

  /**
   * Get the ConfigRequest field model
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @returns {!FieldModelConfigRequest} ConfigRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.MarginRateProfileId.fbeSize + this.MarginRateProfileName.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.MarginRateProfileId.fbeExtra + this.MarginRateProfileName.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelMarginRateProfileGetRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6384
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.MarginRateProfileId.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarginRateProfileId.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginRateProfileId.fbeSize

    if ((fbeCurrentSize + this.MarginRateProfileName.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.MarginRateProfileName.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginRateProfileName.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @param {!MarginRateProfileGetRequest} fbeValue Default value, defaults is new MarginRateProfileGetRequest()
   * @returns {!MarginRateProfileGetRequest} MarginRateProfileGetRequest value
   */
  get (fbeValue = new MarginRateProfileGetRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @param {!MarginRateProfileGetRequest} fbeValue MarginRateProfileGetRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.MarginRateProfileId.fbeSize) <= fbeStructSize) {
      fbeValue.MarginRateProfileId = this.MarginRateProfileId.get()
    } else {
      fbeValue.MarginRateProfileId = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginRateProfileId.fbeSize

    if ((fbeCurrentSize + this.MarginRateProfileName.fbeSize) <= fbeStructSize) {
      fbeValue.MarginRateProfileName = this.MarginRateProfileName.get()
    } else {
      fbeValue.MarginRateProfileName = null
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.MarginRateProfileName.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @param {!MarginRateProfileGetRequest} fbeValue MarginRateProfileGetRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelMarginRateProfileGetRequest}
   * @param {MarginRateProfileGetRequest} fbeValue MarginRateProfileGetRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.MarginRateProfileId.set(fbeValue.MarginRateProfileId)
    this.MarginRateProfileName.set(fbeValue.MarginRateProfileName)
  }
}

export { FieldModelMarginRateProfileGetRequest };

/**
 * Fast Binary Encoding MarginRateProfileGetRequest model
 */
class MarginRateProfileGetRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelMarginRateProfileGetRequest(this.buffer, 4)
  }

  /**
   * Get the MarginRateProfileGetRequest model
   * @this {!MarginRateProfileGetRequestModel}
   * @returns {!FieldModelMarginRateProfileGetRequest} model MarginRateProfileGetRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!MarginRateProfileGetRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!MarginRateProfileGetRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return MarginRateProfileGetRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!MarginRateProfileGetRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelMarginRateProfileGetRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!MarginRateProfileGetRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!MarginRateProfileGetRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!MarginRateProfileGetRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!MarginRateProfileGetRequestModel}
   * @param {!MarginRateProfileGetRequest} value MarginRateProfileGetRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!MarginRateProfileGetRequestModel}
   * @param {!MarginRateProfileGetRequest} value MarginRateProfileGetRequest value, defaults is new MarginRateProfileGetRequest()
   * @return {!object} Deserialized MarginRateProfileGetRequest value and its size
   */
  deserialize (value = new MarginRateProfileGetRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new MarginRateProfileGetRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new MarginRateProfileGetRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!MarginRateProfileGetRequestModel}
   * @param {!number} prev Previous MarginRateProfileGetRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { MarginRateProfileGetRequestModel };
MarginRateProfileGetRequest.__has_response = true;
MarginRateProfileGetRequest.__response_class = MarginRateProfileGetResponse;
